<script>
import formatDate, { formatGetTime } from '@utils/format-date'
import { nlToBr } from '@utils'

export default {
  props: {
    petId: {
      type: [String, Number],
      required: true,
    },
    memberInfo: {
      type: Object,
      required: true,
    },
    products: {
      type: Array,
      default: () => [],
    },
    scheduleDetail: {
      type: Object,
      default: () => {},
    },
    isModified: {
      type: Boolean,
      default: false,
    },
    modifyOrder: {
      type: Object,
      default: () => {},
    },
    petList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      prepaidList: [],
      isBuyPrepaid: false,
      currentBuyPrepaid: {},
      currentPrepaid: {},
      currentPrepaidService: '', // 包月選中的service
      isExpiredDateEdit: false,
      currentPrepaidOrder: {},
      isLoadingPrepaidList: false,
      availablePrepaidListOpen: {}, // 包月列表開啟狀態
      originalExpiredDate: '',
      originalBonusExpiredDate: '',
    }
  },
  computed: {
    multipleStore() {
      return this.products.length > 1
    },
    petSizeId() {
      const pets = this.petList?.length ? this.petList : this.memberInfo?.pets || []
      const pet = pets.find((item) => this.petId === item.id)
      return pet?.sizeId || 0
    },
    filteredServices() {
      // 過濾出適合寵物體型的服務
      if (this.petSizeId) {
        let services = []
        this.products.forEach((product) => {
          const list = product.prices
            .map((priceItem) => ({
              ...priceItem,
              storeTitle: product.title,
              productId: product.id,
            }))
            .filter(({ sizeId }) => sizeId === this.petSizeId)

          services = [...services, ...list]
        })

        return services
      }

      return []
    },
    filteredServicesByPrepaid() {
      // filteredServices 已經濾過體型，再檢查購買的包月裡的服務是否有包含在美容師可以服務的服務裡
      let list = []
      const { times, used, prices, bonusPrices } = this.currentPrepaid
      const primaryFinished = times - used === 0
      const targetPrices = primaryFinished ? bonusPrices : prices

      targetPrices.forEach((item) => {
        this.filteredServices.some((i) => {
          if (i.id === item.id) {
            item = {
              ...item,
              title: item.name,
              productId: i.productId,
              price: i.price,
              serviceTime: i.serviceTime,
            }
            list.push(item)
            return true
          } else {
            return false
          }
        })
      })

      return list
    },
    filteredServicesByBuyingPrepaid() {
      let list = []
      this.currentBuyPrepaid.prices.forEach((item, index) => {
        this.filteredServices.some((i) => {
          if (i.id === item.id) {
            item = {
              ...item,
              productId: i.productId,
              price: i.price,
              serviceTime: i.serviceTime,
            }
            list.push(item)
            return true
          } else {
            return false
          }
        })
      })
      return list
    },
    filterPrepaidList() {
      const priceIds = this.filteredServices.map((item) => item.id)
      // 包套方案因為群組化的關係，不同群組裡可能出現相同的服務
      return this.prepaidList.filter((prepaid) => {
        return prepaid.prices.some((item) => {
          return priceIds.includes(item.id)
        })
      })
    },
    /**
     * @description 可使用的包月列表
     */
    filteredAvailablePrepaidList() {
      const { availablePrepaidOrders = [] } = this.memberInfo
      const petSizeId = this.petSizeId

      // 預先整理每個 product 的 priceId/sizeId，避免重複 map
      const productPriceMap = this.products.flatMap((product) =>
        product.prices.map((price) => ({
          id: price.id,
          sizeId: price.sizeId,
        }))
      )

      // 判斷 prepaid item 是否對應任一 product price + sizeId
      const matchesProduct = (prepaidPrices, prepaidSizeIds) => {
        return prepaidPrices.some((price) => {
          return productPriceMap.some((p) => p.id === price.id && prepaidSizeIds.includes(petSizeId) && p.sizeId === petSizeId)
        })
      }

      // 過濾符合 primary / bonus 條件的 prepaid
      const filtered = availablePrepaidOrders.filter((prepaid) => matchesProduct(prepaid.prices, prepaid.sizeIds) || matchesProduct(prepaid.bonusPrices, prepaid.sizeIds))

      // 根據 id 去重
      return Array.from(new Map(filtered.map((p) => [p.id, p])).values())
    },
    isHasLevelAndDiscountPrice() {
      let result = []
      if (this.filterPrepaidList.length > 0 && this.memberInfo?.level?.prepaids) {
        result = this.filterPrepaidList.reduce((acc, cur) => {
          const index = this.memberInfo.level.prepaids.findIndex((prepaidItem) => prepaidItem.id === cur.id && parseInt(prepaidItem.discount) > 0)
          if (index > -1) {
            // 如果有找到折扣，就走這邊
            const { discountType, discount } = this.memberInfo.level.prepaids[index]
            let price = discountType === '%' ? cur.price - cur.price * (discount / 100) : cur.price - discount
            price = price < 0 ? 0 : price
            acc.push({
              isHasLevel: true,
              price: price,
            })
          } else {
            // 沒有就push原本價錢
            acc.push({
              isHasLevel: false,
              price: cur.price,
            })
          }
          return acc
        }, [])
      }
      return result
    },
  },
  watch: {
    petId() {
      this.fetchPrepaidListWhenReady()
    },
    petList() {
      this.fetchPrepaidListWhenReady()
    },
  },
  mounted() {
    this.fetchPrepaidListWhenReady()
    this.$nextTick(() => {
      this.availablePrepaidListOpen = this.composeAvailablePrepaidListOpen()
    })
  },
  methods: {
    /**
     * @description 製作包月列表開啟編輯列表狀態
     */
    composeAvailablePrepaidListOpen() {
      return this.filteredAvailablePrepaidList.reduce((accu, cur) => {
        accu[cur.id] = false
        return accu
      }, {})
    },
    formatDate,
    nlToBr,
    fetchPrepaidListWhenReady() {
      if (!this.petSizeId) return
      this.tryToFetchPrepaid()
    },
    async tryToFetchPrepaid() {
      if (!this.petSizeId) return
      const time = this.isModified ? `${this.modifyOrder.date} ${this.modifyOrder.time}` : `${formatDate(this.scheduleDetail.date, 'date-dash')} ${formatDate(this.scheduleDetail.date, 'time')}`
      const { items } = await this.$store.dispatch('prepaid/fetchListAll', {
        bookingDateTime: time,
        sizeId: this.petSizeId,
        paginate: 0,
      })
      this.prepaidList = items
    },
    showBuyingPrepaid() {
      if (this.filteredServices.length === 0) {
        this.$confirm(`目前選擇的${this.$t('reserve.CLIENT')}不在排班人員的服務範圍之內`, '提醒', {
          confirmButtonText: '確定',
          showCancelButton: false,
          type: 'warning',
          center: true,
        })
        return
      }

      this.resetData()
      this.isBuyPrepaid = true
      if (this.filterPrepaidList.length > 0) {
        this.selectBuyingPrepaid(this.filterPrepaidList[0])
      }
    },
    closeBuyingPrepaid() {
      this.resetData()
      this.isBuyPrepaid = false
    },
    resetData() {
      this.currentPrepaid = {}
      this.currentPrepaidService = ''
      this.currentBuyPrepaid = {}
    },
    selectPrepaid(item) {
      this.currentPrepaid = { ...item }
      this.currentPrepaidService = ''
      this.currentBuyPrepaid = {}
    },
    selectPrepaidService() {
      if (!this.currentPrepaidService || !Object.keys(this.currentPrepaidService)) {
        return
      }
      const { times, used } = this.currentPrepaid
      const primaryFinished = times - used === 0

      this.$emit('use', {
        prepaid: this.currentPrepaid,
        prepaidService: this.currentPrepaidService,
        isPrepaidBonus: primaryFinished ? 1 : 0,
      })
    },
    selectBuyingPrepaid(item) {
      this.currentBuyPrepaid = { ...item }
      this.currentPrepaidService = ''
    },
    selectBuyingPrepaidService() {
      this.$emit('buy', {
        prepaid: this.currentBuyPrepaid,
        prepaidService: this.currentPrepaidService,
      })
    },
    /**
     * @description 開啟過期時間修改模式
     */
    openExpiredDateEdit(id) {
      this.isExpiredDateEdit = true
      for (const key in this.availablePrepaidListOpen) {
        this.$set(this.availablePrepaidListOpen, key, false)
      }
      this.$set(this.availablePrepaidListOpen, id, true)
      this.tryToFetchPrepaidItem(id)
    },
    async tryToFetchPrepaidItem(id) {
      this.currentPrepaidOrder = await this.$store.dispatch('prepaid/fetchOrderSingle', id)
      this.originalExpiredDate = this.currentPrepaidOrder.expiredDate
      this.originalBonusExpiredDate = this.currentPrepaidOrder.bonusExpiredDate
      this.isExpiredDateEdit = true
    },
    /**
     * @description 取消過期時間修改模式
     */
    cancelExpiredDateEdit(id) {
      this.isExpiredDateEdit = false
      this.$set(this.availablePrepaidListOpen, `${id}`, false)
    },
    /**
     * @description 儲存到期日期
     */
    async tryToStoreExpiredDateEdit(id) {
      try {
        const { extendedExpiredDate } = this.currentPrepaidOrder
        // const bonusExpiredDate = this.currentPrepaidOrder.bonusExpiredDate
        const originalExpiredDate = this.originalExpiredDate || this.currentPrepaidOrder.expiredDate
        // const originalBonusExpiredDate = this.originalBonusExpiredDate || this.currentPrepaidOrder.bonusExpiredDate
        // const primaryServiceCanExtend = expiredDate && new Date(expiredDate).getTime() > new Date(originalExpiredDate).getTime()

        if (new Date(extendedExpiredDate).getTime() < new Date(originalExpiredDate).getTime()) {
          this.$message.error('新的到期日不能早於原本的到期日')
          return
        }
        this.currentPrepaidOrder.extendedExpiredDate = extendedExpiredDate ? `${formatDate(extendedExpiredDate, 'date-dash')} 23:59:59` : null
        await this.tryToSavePrepaidOrder()
        this.isExpiredDateEdit = false
        this.$set(this.availablePrepaidListOpen, id, false)
        if (this.currentPrepaidOrder.id === this.currentPrepaid.id) {
          this.selectPrepaid({})
        }
      } catch (error) {
        console.error(error.message)
      } finally {
        this.$nextTick(() => {
          this.isLoadingPrepaidList = false
        })
      }
    },
    async tryToSavePrepaidOrder() {
      const { params, priceBase, priceTotal, ...other } = this.currentPrepaidOrder
      let storeData = {
        ...other,
        merchantNote: '',
        basePrice: priceBase,
        total: priceTotal,
      }
      if (this.currentPrepaidOrder.paymentMethod === 'Deposit') storeData.paymentStatus = 'PAID'

      this.isLoadingPrepaidList = true
      try {
        await this.$store.dispatch('prepaid/storeOrder', storeData)
        this.$emit('updateMemberInfo', this.memberInfo.id)
        // this.handleSaveSuccess()
      } catch (error) {
        this.$message({
          type: 'error',
          message: `Server Error`,
        })
      } finally {
        // this.isLoadingPrepaidList = false
      }
    },
    isExpiredDate() {
      const { expiredDate, bonusExpiredDate, extendedExpiredDate, times, used } = this.currentPrepaid
      // 購買項目用完就用贈送項目的日期
      const serviceExpiredDate = times - used === 0 ? bonusExpiredDate : expiredDate
      // 有延展日期就用延展後的
      const deadline = extendedExpiredDate || serviceExpiredDate

      return deadline ? new Date() > new Date(formatGetTime(deadline)) : false
    },
    checkIfAllUsed() {
      return this.filteredAvailablePrepaidList.some((item) => item.maxTimes - item.used === 0)
    },
    truncateText(text, length) {
      if (text.length > length) {
        return text.substring(0, length) + '...'
      }
      return text
    },
  },
}
</script>
<template>
  <div :class="$style.orderformSection">
    <div class="orderformSection__all">
      <div class="orderformSection__right" style="border-right: #dcdfe6 1px solid">
        <div class="orderformSection__right__header">
          <button class="el-drawer__close-btn" aria-label="close drawer" type="button" @click.stop="$emit('close')"><i class="el-dialog__close el-icon el-icon-close"></i></button>
          <h4 class="orderformSection__right__title">
            <div class="inline-between" style="padding-right: 15px; align-items: center">
              <span>{{ isBuyPrepaid ? '購買' : '可使用' }}{{ $t('prepaid.BASE') }}方案</span>
              <el-button class="isPrimary orderformSection__right__title__topBtn" style="padding-top: 0; padding-bottom: 0;" @click="!isBuyPrepaid ? showBuyingPrepaid() : closeBuyingPrepaid()">
                {{ !isBuyPrepaid ? '購買' : '可使用' }}{{ $t('prepaid.BASE') }}方案
              </el-button>
            </div>
          </h4>
          <div class="my-2 px-4 text-danger">如需進行{{ $t('prepaid.BASE') }}方案價格編輯與折扣，請先至 {{ $t('prepaid.BASE') }}方案 > 代客購買 處進行，此處無法進行{{ $t('prepaid.EDIT_PRICE') }}</div>
        </div>
        <div v-loading="isLoadingPrepaidList" class="orderformSection__right__content">
          <!-- 使用包月 -->
          <template v-if="filteredAvailablePrepaidList.length > 0 && !isBuyPrepaid">
            <div v-for="item in filteredAvailablePrepaidList" :key="item.id" :class="[$style.price, { isActive: currentPrepaid.id === item.id }]" style="cursor: pointer" @click="selectPrepaid(item)">
              <el-image class="image price__image" :src="item.image" fit="cover">
                <div slot="error">
                  <BaseIcon name="image" class="image--error" />
                </div>
              </el-image>
              <div class="price__cont">
                <div>
                  <div class="price__title">
                    <div v-if="multipleStore" style="padding-bottom: 5px">{{ item.storeTitles.join('，') }} </div> {{ item.prepaidTitle }}
                  </div>
                  <div class="text-muted-0 Jost" style="line-height: 1.6">
                    <!-- 購買項目 -->
                    <template>
                      <span>
                        購買項目可抵用次數：
                        <span class="text-primary">{{ item.times - item.used }}/{{ item.times }}</span>
                      </span>
                      <br />
                      到期日：
                      <!-- 如果開啟修改日期，要開啟edit -->
                      <template v-if="availablePrepaidListOpen[item.id] && currentPrepaidOrder.id === item.id">
                        <el-date-picker v-model="currentPrepaidOrder.extendedExpiredDate" type="date" placeholder="請選擇日期" format="yyyy-MM-dd" value-format="yyyy-MM-dd" class=" mb-2" />
                      </template>
                      <!-- 沒開啟做顯示就走這邊 -->
                      <template v-else>
                        <span v-if="item.extendedExpiredDate">{{ formatDate(item.extendedExpiredDate) }}</span>
                        <span v-else>{{ item.expiredDate ? formatDate(item.expiredDate) : '無使用期限' }}</span>
                      </template>
                    </template>
                    <!-- 日期延展按鈕 -->
                    <template v-if="item.expiredDate && !availablePrepaidListOpen[item.id]">
                      <span style="margin-left: 20px" class="is-round" size="mini" @click="openExpiredDateEdit(item.id)">
                        <BaseIcon name="edit" class="editIconColor" />
                      </span>
                    </template>
                    <!-- 日期開啟顯示取消/儲存 -->
                    <template v-if="availablePrepaidListOpen[item.id]">
                      <br />
                      <el-button class="is-round" size="mini" @click="cancelExpiredDateEdit(item.id)">
                        取消
                      </el-button>
                      <el-button class="is-round" type="primary" size="mini" @click="tryToStoreExpiredDateEdit(item.id)">
                        儲存
                      </el-button>
                    </template>

                    <!-- 贈送項目 -->
                    <template v-if="item.bonusTimes">
                      <br />
                      <span>
                        贈送項目可抵用次數：
                        <span class="text-primary">{{ item.bonusTimes - item.bonusUsed }}/{{ item.bonusTimes }}</span>
                      </span>
                      <br />
                      到期日：
                      <!-- 贈送項目不開放延長 -->
                      <span>{{ item.bonusExpiredDate ? formatDate(item.bonusExpiredDate) : '無使用期限' }}</span>
                    </template>
                    <div v-if="item.note">備註：{{ item.note }}</div>
                    <div v-if="item.status === 2" class="alert-text"> 未付款</div>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <el-alert
            v-if="filteredAvailablePrepaidList.length === 0 && !isBuyPrepaid"
            :title="`目前選擇的${$t('reserve.CLIENT')}與服務人員無適用之${$t('prepaid.BASE')}方案，請調整購買數量、建議顧客購買新的${$t('prepaid.BASE')}方案`"
            type="warning"
            :closable="false"
          ></el-alert>
          <!-- 購買包月 -->
          <template v-if="filterPrepaidList.length > 0 && isBuyPrepaid">
            <div
              v-for="(item, index) in filterPrepaidList"
              :key="item.id"
              :class="[$style.price, { isActive: currentBuyPrepaid.id === item.id }]"
              style="cursor: pointer"
              @click="selectBuyingPrepaid(item)"
            >
              <el-image class="image price__image" :src="item.image" fit="cover">
                <div slot="error">
                  <BaseIcon name="image" class="image--error" />
                </div>
              </el-image>
              <div class="price__cont">
                <div>
                  <div class="price__title">
                    <div v-if="multipleStore" style="padding-bottom: 5px">{{ item.stores.join('，') }} </div> {{ item.title }}
                  </div>
                  <div class="text-muted-0" style="line-height: 1.6">
                    可抵用次數：<span class="Jost text-primary">{{ item.times }}</span
                    ><br />
                    <template v-if="isHasLevelAndDiscountPrice[index]['isHasLevel']">
                      會員價： <span class="Jost">{{ isHasLevelAndDiscountPrice[index]['price'] }}</span>
                      <del style="display: block;" class="line-through-price Jost">售價：{{ item.price }}</del>
                    </template>
                    <template v-else>
                      價格：{{ item.price }}
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="orderformSection__left" style="border: 0">
        <div class="orderformSection__left__content info-section">
          <!-- 使用包月 -->
          <div v-if="currentPrepaid.id && !isBuyPrepaid" :class="$style.prepaid">
            <el-image class="image prepaid__image" :src="currentPrepaid.image" fit="cover">
              <div slot="error">
                <BaseIcon name="image" class="image--error" />
              </div>
            </el-image>
            <div class="d-flex justify-content-between" style="margin-bottom: 40px">
              <span class="prepaid__title ms-0">
                <span v-if="multipleStore">
                  {{ currentPrepaid.storeTitles.join('，') }}
                </span>
                {{ currentPrepaid.prepaidTitle }}<span v-if="isExpiredDate() && !!currentPrepaid.expiredDate" class="text-danger">（已逾期）</span>
              </span>
              <div class="me-0" style="line-height: 1.5;">
                <div class="Jost">購買項目可抵用次數：{{ currentPrepaid.times - currentPrepaid.used }}/{{ currentPrepaid.times }}</div>
                <div v-if="currentPrepaid.bonusTimes" class="Jost">贈送項目可抵用次數：{{ currentPrepaid.bonusTimes - currentPrepaid.bonusUsed }}/{{ currentPrepaid.bonusTimes }}</div>
              </div>
            </div>
            <div style="margin-bottom: 30px">
              <h6>服務項目<span class="text-danger ml-1">*</span></h6>
              <div class="el-form-item--white-bg">
                <div style="display: flex">
                  <el-select v-model="currentPrepaidService" value-key="id">
                    <template v-for="item in filteredServicesByPrepaid">
                      <el-option :key="item.id" :value="item" :label="`${item.storeTitle} ${item.name} `" :disabled="isExpiredDate()">
                        <span style="display: flex; justify-content: space-between;">
                          <span>{{ truncateText(`${item.storeTitle}`, 10) }} {{ truncateText(`${item.name}`, 10) }}</span>
                          <span class="select__option--meta">{{ `${item.isBonus ? '贈送' : '購買'} 尚餘${item.maxTimes - item.used}次` }}</span>
                        </span>
                      </el-option>
                    </template>
                  </el-select>
                  <el-button class="is-round" style="margin-left: 20px" :disabled="!currentPrepaidService && isExpiredDate() && !!currentPrepaid.expiredDate" @click="selectPrepaidService"
                    >使用</el-button
                  >
                </div>
              </div>
            </div>
            <div v-if="currentPrepaid.slogan" style="margin-bottom: 30px">
              <h6>優惠內容</h6>
              <p v-html="nlToBr(currentPrepaid.slogan)"></p>
            </div>
            <div v-if="currentPrepaid.description" style="margin-bottom: 30px">
              <h6>方案內容說明</h6>
              <p v-html="nlToBr(currentPrepaid.description)"></p>
            </div>
            <div v-if="currentPrepaid.notice" style="margin-bottom: 30px">
              <h6>注意事項</h6>
              <p v-html="nlToBr(currentPrepaid.notice)"></p>
            </div>
          </div>
          <!-- 購買包月 -->
          <div v-if="isBuyPrepaid && currentBuyPrepaid.id" :class="$style.prepaid">
            <el-image class="image prepaid__image" :src="currentBuyPrepaid.image" fit="cover">
              <div slot="error">
                <BaseIcon name="image" class="image--error" />
              </div>
            </el-image>
            <div class="inline-between" style="margin-bottom: 40px">
              <span class="prepaid__title">
                <span v-if="multipleStore">
                  {{ currentBuyPrepaid.stores.join('，') }}
                </span>
                {{ currentBuyPrepaid.title }}
              </span>
              <span class="text-muted-0">可抵用次數：{{ currentBuyPrepaid.times }}</span>
            </div>
            <div style="margin-bottom: 30px">
              <h6>可使用服務項目</h6>
              <div class="el-form-item--white-bg">
                <div style="display: flex">
                  <el-select v-model="currentPrepaidService" value-key="id">
                    <el-option v-for="item in filteredServicesByBuyingPrepaid" :key="item.id" :value="item" :label="`${item.storeTitle} ${item.title} `">
                      <span>{{ `${item.storeTitle} ${item.title}` }}</span>
                      <span class="select__option--meta">{{ `(可抵用次數${item.maxTimes}次)` }}</span>
                    </el-option>
                  </el-select>
                  <el-button class="is-round" style="margin-left: 20px" :disabled="!currentPrepaidService" @click="selectBuyingPrepaidService">使用方案</el-button>
                </div>
              </div>
            </div>
            <div v-if="currentBuyPrepaid.slogan" style="margin-bottom: 30px">
              <h6>優惠內容</h6>
              <p v-html="nlToBr(currentBuyPrepaid.slogan)"></p>
            </div>
            <div v-if="currentBuyPrepaid.description" style="margin-bottom: 30px">
              <h6>方案內容說明</h6>
              <p v-html="nlToBr(currentBuyPrepaid.description)"></p>
            </div>
            <div v-if="currentBuyPrepaid.notice" style="margin-bottom: 30px">
              <h6>注意事項</h6>
              <p v-html="nlToBr(currentBuyPrepaid.notice)"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" module>

.orderformSection {
  :global {
    .editIconColor {
      color: $color-primary;
    }

    .orderformSection {
      &__all {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        height: 100%;
        // display: none;
      }

      &__left {
        width: 57%;
        border-right: $color-border-base 1px solid;
        display: flex;
        flex-direction: column;
        justify-content: stretch;
        background: $color-line;

        &__content {
          height: 100vh;
          overflow: auto;

          padding-bottom: 90px;
        }
      }

      &__right {
        position: relative;
        width: 43%;
        height: calc(100vh);
        overflow: hidden;
        background: $color-line;

        .orderformSection__right__header {
          background-color: $color-line;
          z-index: 999;
        }

        &__title {
          margin: 0;
          padding: 19px 0;
          padding-left: 50px;

          &__topBtn {
            border-radius: $border-radius;
          }
        }

        &__header {
          position: relative;
          border-bottom: $color-border-base 1px solid;

          .el-drawer__close-btn {
            // font-weight: 300;
            color: #72767b;
            position: absolute;
            padding: 15px;
            border: 0;
            left: 0;
            top: 15px;
          }

          .el-tabs__item {
            height: auto;
            padding: 10px 0;
          }

          .el-tabs__nav-wrap::after {
            display: none;
          }

          .el-tabs__header {
            margin-bottom: 0;
          }

          .el-tabs--top {
            margin-left: 60px;

            @include max-lg {
              margin-left: 30px;
            }
          }
        }

        &__content {
          z-index: 10;
          height: calc(100vh - 60px);
          overflow: auto;
          padding: 30px;
          padding-bottom: 120px;
          position: relative;
          background-color: $color-line;

          @include max-lg {
            padding: 20px;
          }
        }
      }
    }
  }
}

// 服務
.price {
  display: flex;
  margin-bottom: 15px;
  padding: 16px 18px;
  background-color: white;
  border-radius: $border-radius;
  border: $color-border-base 1px solid;

  &:global {
    &:hover {
      .price__title {
        color: $color-primary;
        transition: all 0.35s;
      }
    }

    &.isActive {
      border: $color-primary 1px solid;

      .price__title {
        color: $color-primary;
        // transition: all 0.35s;
      }
    }

    &.isDisabled {
      pointer-events: none;
    }
  }

  :global {
    .price {
      &__image {
        width: 50%;
        height: 10vw;
        border: 0;
        border-radius: 8px;

        @include max-md {
          height: auto;
        }
      }

      &__cont {
        width: 50%;
        padding-left: 15px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      &__title {
        font-weight: 500;
        margin-bottom: 10px;
        padding-top: 10px;
      }
    }

    .text-muted-0 {
      font-size: 14px;
    }
  }
}

.prepaid {
  :global {
    .prepaid {
      &__image {
        width: 100%;
        height: 20vw;
        border: 0;
        margin-bottom: 10px;
      }

      &__title {
        font-weight: 500;
      }
    }
  }
}
</style>
